import { useAuthStore } from '../stores/authStore';
import type { UserRole } from '../types/auth.types';

/**
 * Custom hook providing clean API to access authentication state and actions
 * @returns Auth state and methods for components
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isInitialized,
    loading,
    error,
    redirectPath,
    login,
    logout,
    register,
    refreshAuthToken,
    checkAuth,
    clearError,
    setRedirectPath,
    signInWithOAuth,
    completeOAuthSession,
  } = useAuthStore();

  /**
   * Check if current user has specific role
   * @param role - Role to check against current user
   * @returns True if user has the specified role
   */
  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  /**
   * Check if current user has any of the specified roles
   * @param roles - Array of roles to check
   * @returns True if user has any of the specified roles
   */
  const hasAnyRole = (roles: readonly UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  /**
   * Get user's role or null if not authenticated
   * @returns Current user's role or null
   */
  const getRole = (): UserRole | null => {
    return user?.role ?? null;
  };

  return {
    // State
    user,
    role: getRole(),
    isAuthenticated,
    isInitialized,
    loading,
    error,
    redirectPath,

    // Actions
    login,
    logout,
    register,
    refreshAuthToken,
    checkAuth,
    clearError,
    setRedirectPath,
    signInWithOAuth,
    completeOAuthSession,

    // Helpers
    hasRole,
    hasAnyRole,
  };
}
